/**
 * MACHINE-V2 — readable Unit dossier dock (sheet rows, calm meters).
 * Soft≠Live: Live fields only when reported; Save = client + backend guards.
 *
 * Skills · runtime F1 (2026-08-19):
 *   A · Running / Staged — device telemetry projection only
 *   B · Market — catalog available skills (never Running)
 *   C · Board image — tenant peek only; SA executes full OTA
 * Honesty: view-only until device_ops_allowed · HTTP accept is not success
 *          · poll current job_id until ACK then telemetry · Re-read runtime
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { html, M, Ico, Alert, SOFT, stop, useEsc, useOverlayLock, cmcPlainOf, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { boardKindSay, operatorSay } from "./operator-say.js";
import { bumpLiveZones } from "./live.js";
import { sitesOfDevice } from "./ops-map-region-filter.js";
import { skillLabelOf, skillSayOf } from "./machine-skill-labels.js";
import {
  edgeSkillCatalogListPath,
  edgeSkillCatalogStatusPath,
  edgeSkillCatalogStatusViewOf,
  edgeSkillCatalogViewOf,
  resolveEdgeSkillCatalogRows,
} from "./machine-edge-skills-catalog.js";
import {
  edgeSkillRuntimeStatusPath,
  edgeSkillRuntimeStatusViewOf,
  edgeSkillRuntimeDevicePath,
  edgeSkillRuntimeDeviceViewOf,
  edgeSkillRuntimeDeviceJobsPath,
  edgeSkillRuntimeJobsViewOf,
  edgeSkillRuntimeArtifactsPath,
  edgeSkillRuntimeArtifactsViewOf,
  edgeSkillRuntimeInstallPath,
  edgeSkillRuntimeActivatePath,
  edgeSkillRuntimeRemovePath,
  edgeSkillRuntimeRailsOf,
  edgeSkillRuntimeActionGateOf,
  edgeSkillRuntimeActivateGateOf,
  edgeSkillRuntimeRemoveGateOf,
  edgeSkillRuntimeInstallGateOf,
  edgeSkillRuntimeArtifactForSkill,
  edgeSkillRuntimeInventorySay,
  edgeSkillRuntimeOpsAllowed,
  edgeSkillRuntimeViewOnly,
  edgeSkillRuntimeRunCommand,
  EDGE_SKILL_RUNTIME_DISPATCHED,
  EDGE_SKILL_RUNTIME_INSTALL_REFUSAL,
} from "./machine-edge-skills-runtime.js";
import {
  softEstateDetailOf, softEstatePinsOf, softEstateZonesOf,
} from "./machine-soft-estate.js";
import ZoneMiniMap, { pointInZone } from "./zone-mini-map.js";
import { softNetGet, softNetPut, softWifiScanViaLan } from "./machine-soft-net-store.js";
import MachineInspectOps from "./machine-inspect-ops.js";
import { EstatePick, EditRow, FactRow } from "./machine-estate-controls.js";
import { storageCardViewOf } from "./edgeos-live.js";
import { formatCard, mountCard, unmountCard } from "./board-api.js";
import { isLocalHost } from "./pathseal.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import {
  scanHitsFromJob,
  useWifiOpsStatus,
  wifiJobViewOf,
  wifiOpsIdempotencyKey,
  wifiOpsPath,
  wifiOpsPollJob,
  wifiOpsStatusViewOf,
  wifiSwitchedSay,
} from "./machine-wifi-ops.js";


function numOf(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function locationPointOf(detail, device) {
  const raw = objectOf(device && device.raw);
  const lat = numOf(detail && detail.map_lat, raw.map_lat);
  const lng = numOf(detail && detail.map_lng, raw.map_lng);
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lng, lat];
}

function locationSourceSay(src) {
  const s = String(src || "").trim().toUpperCase();
  if (!s) return "Not reported";
  if (s === "GNSS" || s === "GPS" || s === "BOARD_GPS") return "Firmware GNSS";
  if (s === "INSTALL_SEAL") return "Sealed at install from this phone";
  if (s === "L2_BROTHER") return "Same-LAN brother · site pin · not metres";
  if (s === "MANUAL" || s === "MAP_PIN" || s === "OPERATOR") return "Manual pin";
  if (s === "GEOIP" || s === "IP_GUESS") return "IP guess · hold";
  return operatorSay(src, s.replace(/_/g, " "));
}

function fmtCoord(point) {
  if (!Array.isArray(point) || point.length < 2) return "—";
  const lat = Number(point[1]);
  const lng = Number(point[0]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "—";
  return lat.toFixed(5) + ", " + lng.toFixed(5);
}

function fmtMb(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1024) return (n / 1024).toFixed(1) + " GB";
  return Math.round(n) + " MB";
}

function zonesPackOf(pack) {
  const rows = (pack && (pack.items || pack.zones)) || [];
  return Array.isArray(rows)
    ? rows.filter((z) => z && Array.isArray(z.ring) && z.ring.length >= 3)
    : [];
}

function safeCardPath(did) {
  const id = String(did || "").trim();
  if (!id) return "";
  try {
    return path("get__devices_p_storage_card", { pathParams: [id] });
  } catch {
    return "/devices/" + encodeURIComponent(id) + "/storage/card";
  }
}

/* MACHINE-LOC-TF-919 */

function textOf(...values) {
  for (const value of values) {
    const text = String(value == null ? "" : value).trim();
    if (text) return text;
  }
  return "";
}

function mergeSoftDetail(live, soft) {
  const L = live && typeof live === "object" ? live : {};
  const S = soft && typeof soft === "object" ? soft : null;
  if (!S) return L;
  const out = { ...S, ...L };
  for (const key of Object.keys(S)) {
    const lv = L[key];
    const empty = lv == null || lv === "" || lv === "—" || lv === false
      || (typeof lv === "object" && !Array.isArray(lv) && !Object.keys(lv).length);
    if (empty && S[key] != null && S[key] !== "") out[key] = S[key];
  }
  return out;
}

function objectOf(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function stateOf(value) {
  if (value === true) return { label: "Active", tone: "go" };
  if (value === false) return { label: "Off", tone: "bad" };
  const text = textOf(value).toLowerCase();
  if (!text) return { label: "Not reported", tone: "dim" };
  if (["online", "active", "ready", "connected", "running", "healthy", "up", "enabled"].includes(text)) {
    return { label: operatorSay(text, text.replace(/_/g, " ")), tone: "go" };
  }
  if (["off", "offline", "down", "failed", "error", "quiet", "disconnected", "stopped", "disabled", "unhealthy"].includes(text)) {
    return { label: operatorSay(text, text.replace(/_/g, " ")), tone: "bad" };
  }
  return { label: operatorSay(text, text.replace(/_/g, " ")), tone: "dim" };
}

function linkKindOf(detail, device, softMedium) {
  if (softMedium) {
    const m = String(softMedium).toLowerCase();
    if (m === "wifi" || m === "wi-fi") return "wifi";
    if (m === "poe") return "poe";
    if (m === "lan" || m === "ethernet") return "lan";
  }
  const raw = objectOf(device && device.raw);
  const net = objectOf(detail.net_health);
  const candidates = [
    detail.link, detail.link_type, detail.net_type, detail.transport,
    net.link, net.net_type, net.medium,
    device && device.net, raw.net_type, raw.link,
  ].map((v) => String(v || "").trim().toLowerCase()).filter(Boolean);
  for (const c of candidates) {
    if (/wifi|wlan|sta/.test(c)) return "wifi";
    if (/poe/.test(c)) return "poe";
    if (/eth|lan|wired|ethernet/.test(c)) return "lan";
  }
  return "";
}

/** True only when Live reports a wired/LAN side in addition to (or instead of) Wi-Fi. */
function lanSideLiveOf(detail, device, net) {
  const d = detail && typeof detail === "object" ? detail : {};
  const n = net && typeof net === "object" ? net : {};
  const raw = objectOf(device && device.raw);
  const flags = [
    d.lan_up, d.ethernet_up, d.wired_up, d.lan_connected, d.ethernet_connected,
    n.lan_up, n.ethernet_up, n.wired_up,
    raw.lan_up, raw.ethernet_up,
  ];
  for (const v of flags) {
    if (v === true) return true;
    const t = String(v || "").trim().toLowerCase();
    if (t === "1" || t === "up" || t === "connected" || t === "active" || t === "yes") return true;
  }
  const lanIp = textOf(d.lan_ip, d.ethernet_ip, n.lan_ip, n.ethernet_ip, raw.lan_ip);
  const wifiIp = textOf(d.wifi_ip, n.wifi_ip, raw.wifi_ip);
  if (lanIp && wifiIp && lanIp !== wifiIp) return true;
  if (lanIp && linkKindOf(d, device, "") === "wifi") return true;
  return false;
}

/** Operator Link fact: Wi-Fi alone; Wi-Fi · LAN only if wired also Live; LAN; PoE/LAN. */
function linkFactLabelOf(detail, device, net) {
  const kind = linkKindOf(detail, device, "");
  if (kind === "poe") return "PoE/LAN";
  if (kind === "lan") return "LAN";
  if (kind === "wifi") {
    return lanSideLiveOf(detail, device, net) ? "Wi-Fi · LAN" : "Wi-Fi";
  }
  return "—";
}

function profileClientGuard(label, group) {
  const name = String(label || "").trim();
  if (!name) return { ok: false, error: "Name is required" };
  if (name.length > 64) return { ok: false, error: "Name must be 64 characters or fewer" };
  const g = String(group || "").trim();
  if (g.length > 64) return { ok: false, error: "Dispatch group must be 64 characters or fewer" };
  return { ok: true, label: name, group: g };
}

export default function MachineQuickDrawer({
  device, onClose, toast, docked = false, capsSeed = null, onCapsSaved,
  groupOptions = [],
}) {
  const local = isLocalHost();
  const did = String((device && device.id) || "").trim();
  const posture = useApi((did && !local) ? path("get__fleet_device_status") : "");
  const cmdOk = posture.loading ? false : (
    !!(posture.data && posture.data.commands_allowed === true)
  );
  const writeOk = posture.loading ? false : (
    !!(posture.data && (
      posture.data.profile_write_allowed === true || posture.data.write_allowed === true
    ))
  );
  const softDetail = local ? softEstateDetailOf(did) : null;
  const softPins = useMemo(() => (local ? softEstatePinsOf(did) : []), [local, did]);
  const detailApi = useApi(softDetail ? null : (did ? path("get__devices_p", { pathParams: [did] }) : null));
  const detail = useMemo(
    () => mergeSoftDetail(objectOf(detailApi.data), softDetail),
    [detailApi.data, softDetail],
  );
  const net = objectOf(detail.net_health);
  const gatewayObs = objectOf(detail.gateway_observation);
  const udockRaw = objectOf(detail.udock);
  const meshRaw = objectOf(detail.mesh);
  const catalogStatusApi = useApi(softDetail ? null : edgeSkillCatalogStatusPath());
  const catalogStatus = useMemo(
    () => edgeSkillCatalogStatusViewOf(catalogStatusApi.data),
    [catalogStatusApi.data],
  );
  const catalogListApi = useApi(
    softDetail || !did || !catalogStatus.listAllowed
      ? null
      : edgeSkillCatalogListPath({ deviceId: did }),
  );
  const catalogView = useMemo(
    () => edgeSkillCatalogViewOf(catalogListApi.data),
    [catalogListApi.data],
  );
  const catalogPack = useMemo(
    () => resolveEdgeSkillCatalogRows({
      statusView: catalogStatus,
      catalogView,
      catalogError: catalogListApi.error,
      local,
    }),
    [catalogStatus, catalogView, catalogListApi.error, local],
  );
  const runtimeStatusApi = useApi(softDetail ? null : edgeSkillRuntimeStatusPath());
  const runtimeStatus = useMemo(
    () => edgeSkillRuntimeStatusViewOf(runtimeStatusApi.data),
    [runtimeStatusApi.data],
  );
  const runtimeProductOn = runtimeStatus.productEnabled === true;
  const runtimeDeviceApi = useApi(
    softDetail || !did || !runtimeProductOn ? null : edgeSkillRuntimeDevicePath(did),
  );
  const runtimeView = useMemo(
    () => edgeSkillRuntimeDeviceViewOf(runtimeDeviceApi.data),
    [runtimeDeviceApi.data],
  );
  const runtimeOpsOn = edgeSkillRuntimeOpsAllowed(runtimeStatus, runtimeView);
  const runtimeViewOnly = edgeSkillRuntimeViewOnly(runtimeStatus, runtimeView);
  const runtimeJobsApi = useApi(
    softDetail || !did || !runtimeProductOn ? null : edgeSkillRuntimeDeviceJobsPath(did, { limit: 50 }),
  );
  const runtimeJobsView = useMemo(
    () => edgeSkillRuntimeJobsViewOf(runtimeJobsApi.data),
    [runtimeJobsApi.data],
  );
  const runtimeArtifactsApi = useApi(
    softDetail || !runtimeProductOn ? null : edgeSkillRuntimeArtifactsPath(),
  );
  const runtimeArtifactsView = useMemo(
    () => edgeSkillRuntimeArtifactsViewOf(runtimeArtifactsApi.data),
    [runtimeArtifactsApi.data],
  );
  const runtimeRails = useMemo(
    () => edgeSkillRuntimeRailsOf({
      runtimeView,
      catalogRows: catalogPack.rows,
      jobsView: runtimeJobsView,
    }),
    [runtimeView, catalogPack.rows, runtimeJobsView],
  );
  const pendingIdemRef = useRef({});
  const boardPins = useApi(softPins.length ? null : (did ? path("get__devices_p_pins", { pathParams: [did] }) : null));
  const seats = softPins.length
    ? softPins
    : ((boardPins.data && (boardPins.data.items || boardPins.data.pins || boardPins.data.seats)) || []);

  const softCard = objectOf(softDetail && softDetail.storage_card);
  const softHasCard = softDetail != null && (softCard.present != null || softCard.mounted != null);
  const cardApi = useApi(softHasCard ? null : (did ? safeCardPath(did) : null));
  const card = useMemo(() => {
    if (softHasCard) {
      return {
        present: softCard.present === true,
        mounted: softCard.mounted === true,
        fs: String(softCard.fs || "").trim() || "",
        capacityMb: softCard.capacity_mb != null && Number.isFinite(Number(softCard.capacity_mb))
          ? Number(softCard.capacity_mb) : null,
        freeMb: softCard.free_mb != null && Number.isFinite(Number(softCard.free_mb))
          ? Number(softCard.free_mb) : null,
      };
    }
    return storageCardViewOf(cardApi.data);
  }, [softHasCard, softCard, cardApi.data]);
  const tfPresence = card
    ? (card.mounted ? "Mounted" : card.present ? "Present" : (card.present === false ? "Empty" : "—"))
    : "—";
  const tfCap = card && card.capacityMb != null ? card.capacityMb : null;
  const tfFree = card && card.freeMb != null ? card.freeMb : null;
  const tfUsedPct = (tfCap != null && tfCap > 0 && tfFree != null)
    ? Math.max(0, Math.min(100, Math.round(((tfCap - tfFree) / tfCap) * 100)))
    : null;
  const tfMeterSay = (tfCap != null || tfFree != null)
    ? [
        tfUsedPct != null ? (tfUsedPct + "% used") : null,
        tfFree != null ? ("Free " + fmtMb(tfFree)) : null,
        tfCap != null ? ("Cap " + fmtMb(tfCap)) : null,
        card && card.fs ? card.fs : null,
      ].filter(Boolean).join(" · ")
    : "No capacity reported";

  const runTfCard = async (kind) => {
    if (!did) return;
    if (!local && !cmdOk) {
      toast && toast("Commands closed for this seat", "warn");
      return;
    }
    const labels = {
      format: { confirm: "Format TF", detail: "Erase this TF card and lay down a fresh FAT volume. Dispatch is not success — wait for device ACK and capacity telemetry." },
      mount: { confirm: "Mount TF", detail: "Mount the TF card on this board. Dispatch is not success — wait for device ACK and capacity telemetry." },
      unmount: { confirm: "Unmount TF", detail: "Unmount the TF card before physical removal. Dispatch is not success — wait for device ACK." },
    };
    const meta = labels[kind];
    if (!meta) return;
    const ok = await confirmDeviceCommand({
      kind: "hold",
      targets: [{ id: did, label: String((device && device.label) || did) }],
      detail: meta.detail,
      confirmLabel: meta.confirm,
    });
    if (!ok) return;
    if (local) {
      const done = { format: "formatted", mount: "mounted", unmount: "unmounted" }[kind];
      toast && toast("TF " + kind + " · local preview · not Live · not sent · " + done, "warn");
      return;
    }
    const reason = "Machine drawer · TF card " + kind;
    const out = kind === "format"
      ? await formatCard(did, reason)
      : kind === "unmount"
        ? await unmountCard(did, reason)
        : await mountCard(did, reason);
    if (!out || !out.ok) {
      toast && toast((out && out.error) || ("TF " + kind + " refused"), "bad");
      return;
    }
    const dispatch = out.data && out.data.dispatch;
    if (dispatch && dispatch.dispatched === false) {
      toast && toast(dispatch.error || ("TF " + kind + " not on the wire"), "bad");
      return;
    }
    toast && toast(
      meta.confirm + " dispatched · waiting device ACK + TF telemetry",
      "wait",
    );
    cardApi.reload && cardApi.reload();
  };
  const formatTf = () => runTfCard("format");
  const mountTf = () => runTfCard("mount");
  const unmountTf = () => runTfCard("unmount");

  const act = useAction();
  const wifiOpsApi = useWifiOpsStatus(local ? "" : did);
  const wifiOpsView = useMemo(() => wifiOpsStatusViewOf(wifiOpsApi.data), [wifiOpsApi.data]);
  const wifiJobView = useMemo(
    () => wifiJobViewOf(wifiOpsView && wifiOpsView.inflight),
    [wifiOpsView],
  );
  const wifiSwitchSay = useMemo(
    () => wifiSwitchedSay(wifiOpsView, wifiJobView),
    [wifiOpsView, wifiJobView],
  );
  const [label, setLabel] = useState(String((device && device.label) || ""));
  const [group, setGroup] = useState(String((device && device.group) || ""));
  const [site, setSite] = useState("");
  const [zone, setZone] = useState("");
  const [ssid, setSsid] = useState("");
  const [psk, setPsk] = useState("");
  const [dirty, setDirty] = useState(false);
  const [netOpen, setNetOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const [wifiDirty, setWifiDirty] = useState(false);
  /** password = same network · join = change SSID */
  const [wifiIntent, setWifiIntent] = useState("password");
  const [wifiScan, setWifiScan] = useState([]);
  const [wifiScanBusy, setWifiScanBusy] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [locDirty, setLocDirty] = useState(false);
  const [point, setPoint] = useState(null);
  const [zoneId, setZoneId] = useState("");
  const [latDraft, setLatDraft] = useState("");
  const [lngDraft, setLngDraft] = useState("");
  const [editId, setEditId] = useState("");
  const [fieldStatus, setFieldStatus] = useState({});

  useEsc(onClose);
  useOverlayLock(!docked);

  const setStatus = (id, next) => {
    setFieldStatus((prev) => ({ ...prev, [id]: next }));
  };

  const toggleEdit = (id) => {
    setEditId((cur) => (cur === id || id === "" ? "" : id));
  };

  const runDispatch = async (id, runner) => {
    setStatus(id, { tone: "send", say: "Saving…" });
    try {
      const result = await runner();
      const tone = (result && result.tone) || (result && result.ok ? "go" : "bad");
      const say = (result && result.say) || (result && result.ok ? "Saved" : "Failed");
      setStatus(id, { tone, say });
      if (result && result.ok) setEditId("");
      toast && toast(say, tone === "go" ? "go" : tone === "warn" ? "warn" : "bad");
      return result;
    } catch (e) {
      const say = "Save failed · " + cmcPlainOf(e);
      setStatus(id, { tone: "bad", say });
      toast && toast(say, "bad");
      return { ok: false, tone: "bad", say };
    }
  };

  const liveIp = textOf(detail.ip, detail.ip_address, detail.lan_ip, detail.wifi_ip, net.ip, net.ip_address, gatewayObs.ip);
  const liveGw = textOf(detail.gateway, detail.gw, net.gateway, net.gw, gatewayObs.gateway, gatewayObs.gw);
  const liveMask = textOf(detail.subnet, detail.subnet_mask, detail.netmask, net.subnet, net.netmask, gatewayObs.subnet, gatewayObs.netmask);

  useEffect(() => {
    setLabel(String((device && device.label) || ""));
    setGroup(String((device && device.group) || ""));
    setDirty(false);
    setEditId("");
    setFieldStatus({});
    setPsk("");
    setLocOpen(false);
    setLocDirty(false);
  }, [did]);

  useEffect(() => {
    if (dirty) return;
    if (!detailApi.data && !softDetail) return;
    setLabel(textOf(detail.display_label, detail.map_label, device && device.label));
    setGroup(textOf(detail.notification_group, detail.group, device && device.group));
  }, [detailApi.data, softDetail, dirty, device && device.label, device && device.group, detail.display_label, detail.notification_group, detail.map_label, detail.group]);

  const machineHome = sitesOfDevice(local ? detail : {
    site_id: textOf(detail.site_id, device && device.raw && device.raw.site_id, device && device.site_id),
    site: textOf(device && device.site),
  })[0] || "";

  useEffect(() => {
    setSite(machineHome);
    setZone(textOf(detail.zone, device && device.zone));
  }, [machineHome, detail.zone, device && device.zone]);

  const savedPoint = useMemo(() => locationPointOf(detail, device), [
    detail.map_lat, detail.map_lng, device && device.raw && device.raw.map_lat, device && device.raw && device.raw.map_lng,
  ]);
  const locSource = textOf(
    detail.location_source,
    device && device.raw && device.raw.location_source,
  );
  const siteForZones = machineHome;
  let zonesPath = null;
  if (!local && did && siteForZones) {
    try {
      zonesPath = path("get__zones", { query: { site_id: siteForZones } });
    } catch (_) {
      zonesPath = "/zones?site_id=" + encodeURIComponent(siteForZones);
    }
  }
  const zonesApi = useApi(zonesPath);
  const zones = useMemo(
    () => (local ? softEstateZonesOf(siteForZones) : zonesPackOf(zonesApi.data)),
    [local, siteForZones, zonesApi.data],
  );
  const selectedZone = zones.find((z) => String(z.zone_id || z.id || "") === String(zoneId || "")) || null;
  const pointInside = !!(selectedZone && pointInZone(point, selectedZone.ring));

  useEffect(() => {
    if (locDirty) return;
    setPoint(savedPoint);
    setLatDraft(savedPoint ? String(Number(savedPoint[1]).toFixed(5)) : "");
    setLngDraft(savedPoint ? String(Number(savedPoint[0]).toFixed(5)) : "");
    const match = zones.find((z) => {
      const id = String(z.zone_id || z.id || "");
      const name = String(z.label || "");
      return id === zone || name === zone || pointInZone(savedPoint, z.ring);
    });
    setZoneId(match ? String(match.zone_id || match.id || "") : (zones[0] ? String(zones[0].zone_id || zones[0].id || "") : ""));
  }, [savedPoint, zones, zone, locDirty]);

  const applyDraftPoint = (nextLat, nextLng) => {
    const lat = Number(nextLat);
    const lng = Number(nextLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      toast && toast("Lat/Lng out of range", "bad");
      return;
    }
    const next = [lng, lat];
    if (selectedZone && !pointInZone(next, selectedZone.ring)) {
      toast && toast("That point is outside the selected Zone", "warn");
    }
    setPoint(next);
    setLocDirty(true);
  };

  const saveLocation = async () => {
    if (!did) return { ok: false };
    if (!point) {
      toast && toast("Enter or tap a location first", "warn");
      return { ok: false, tone: "warn", say: "No point" };
    }
    if (selectedZone && !pointInZone(point, selectedZone.ring)) {
      toast && toast("Place the machine inside the selected Zone first", "warn");
      return { ok: false, tone: "warn", say: "Outside zone" };
    }
    if (local) {
      setLocDirty(false);
      return { ok: true, tone: "warn", say: "Location saved locally · map write not available here" };
    }
    if (!writeOk) {
      return { ok: false, tone: "warn", say: "Place edit closed for this seat" };
    }
    const body = {
      map_lng: Number(point[0]),
      map_lat: Number(point[1]),
      map_label: selectedZone
        ? String(selectedZone.label || selectedZone.zone_id || selectedZone.id || "")
        : textOf(detail.map_label, zone),
    };
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [did] }), {
      method: "PATCH",
      body,
    });
    if (!out) return { ok: false, tone: "bad", say: act.error || "Placement refused" };
    setLocDirty(false);
    detailApi.reload && detailApi.reload();
    zonesApi.reload && zonesApi.reload();
    bumpLiveZones({ reason: "machine-placement", deviceId: did });
    return { ok: true, tone: "go", say: "Location saved · Zone from geometry" };
  };

  useEffect(() => {
    if (wifiDirty) return;
    const soft = local ? (softNetGet(did) || {}) : {};
    const reported = textOf(detail.ssid);
    setSsid(soft.ssid || reported);
    setWifiIntent(reported ? "password" : "join");
  }, [did, detail.ssid, wifiDirty, local]);

  const mac = textOf(detail.mac_display, detail.mac, detail.mac_nocolon);
  const fw = textOf(detail.fw, device && device.fw);
  const fwHint = objectOf(detail.firmware_hint);
  const fwLatest = textOf(
    fwHint.to_version, detail.fw_latest, detail.ota_latest, detail.latest_fw,
  );
  const fwNotes = textOf(fwHint.release_notes, detail.fw_release_notes, detail.release_notes);
  const upgradeOn = fwHint.update_available === true
    || detail.upgrade_available === true
    || (fwLatest && fw && fwLatest !== fw);
  const edgeAi = stateOf(
    detail.edge_ai_state != null ? detail.edge_ai_state
      : detail.edge_ai_enabled != null ? detail.edge_ai_enabled
        : detail.ai_runtime_state,
  );
  const udock = stateOf(
    detail.udock_state != null ? detail.udock_state
      : udockRaw.state != null ? udockRaw.state
        : udockRaw.connected,
  );
  const mesh = stateOf(
    detail.mesh_state != null ? detail.mesh_state
      : meshRaw.state != null ? meshRaw.state
        : meshRaw.connected,
  );

  const reloadRuntime = () => {
    runtimeDeviceApi.reload && runtimeDeviceApi.reload();
    runtimeJobsApi.reload && runtimeJobsApi.reload();
    runtimeArtifactsApi.reload && runtimeArtifactsApi.reload();
  };

  const readRuntimeJobs = async () => act.run(
    edgeSkillRuntimeDeviceJobsPath(did, { limit: 50 }),
    { method: "GET" },
  );
  const readRuntimeDevice = async () => act.run(
    edgeSkillRuntimeDevicePath(did),
    { method: "GET" },
  );

  const runtimeGateCtx = (skillId, catalogRow, artifact) => ({
    statusView: runtimeStatus,
    runtimeView,
    inventory: runtimeView.inventory,
    skillId,
    catalogRow,
    artifact,
  });

  const toastRuntimeResult = (result) => {
    if (!result) return;
    if (result.phase === "dispatch" && result.httpAccepted) {
      toast && toast(EDGE_SKILL_RUNTIME_DISPATCHED, "warn");
      return;
    }
    if (result.ok) {
      const tone = result.telemetryObserved ? "go" : "warn";
      toast && toast(result.say, tone);
      return;
    }
    const tone = result.timedOut ? "warn" : "bad";
    toast && toast(result.say, tone);
  };

  const runtimeDispatch = async ({
    action, skillId, artifactId, purge, confirmDetail, confirmLabel, gate,
  }) => {
    if (!did) return { ok: false, say: "No unit selected" };
    const g = gate || edgeSkillRuntimeActionGateOf(action, runtimeGateCtx(skillId));
    if (!g.allowed) {
      toast && toast(g.label, "warn");
      return { ok: false, say: g.label };
    }
    if (confirmDetail) {
      const ok = await confirmDeviceCommand({
        kind: action,
        targets: [{ id: did, label: String(label || did) }],
        detail: confirmDetail,
        confirmLabel: confirmLabel || "Confirm",
      });
      if (!ok) return { ok: false, say: "Cancelled" };
    }
    let url = "";
    if (action === "install") url = edgeSkillRuntimeInstallPath(did);
    else if (action === "activate") url = edgeSkillRuntimeActivatePath(did);
    else if (action === "remove") url = edgeSkillRuntimeRemovePath(did);

    const result = await edgeSkillRuntimeRunCommand({
      action,
      skillId,
      artifactId,
      purge,
      idempotencyStore: pendingIdemRef.current,
      skipTelemetry: action === "remove",
      expectedTelemetryStates: action === "activate" ? ["active"] : ["active", "staged"],
      dispatch: async ({ headers, body }) => act.run(url, {
        method: "POST",
        headers,
        body,
      }),
      readJobs: readRuntimeJobs,
      readRuntime: readRuntimeDevice,
    });
    reloadRuntime();
    toastRuntimeResult(result);
    return result;
  };

  const installCatalogSkill = async (item) => {
    const skillId = String(item.skillId || item.id || "").trim();
    const artifact = edgeSkillRuntimeArtifactForSkill(runtimeArtifactsView, skillId);
    const gate = edgeSkillRuntimeInstallGateOf(runtimeGateCtx(skillId, item, artifact));
    await runtimeDispatch({
      action: "install",
      skillId,
      artifactId: artifact && artifact.artifactId,
      gate,
      confirmDetail: "Hot-update install with activate:true · board pulls artifact after ACK.",
      confirmLabel: "Install skill",
    });
  };

  const activateStagedSkill = async (skillId) => {
    const gate = edgeSkillRuntimeActivateGateOf(runtimeGateCtx(skillId));
    await runtimeDispatch({
      action: "activate",
      skillId,
      gate,
      confirmDetail: "Activate staged skill on this board · waits for device ACK.",
      confirmLabel: "Activate skill",
    });
  };

  const removeActiveSkill = async (skillId) => {
    const gate = edgeSkillRuntimeRemoveGateOf(runtimeGateCtx(skillId));
    await runtimeDispatch({
      action: "remove",
      skillId,
      purge: false,
      gate,
      confirmDetail: "Remove active skill from runtime · purge:false keeps artifact on flash.",
      confirmLabel: "Remove skill",
    });
  };

  const catalogRowOf = (skillId) => catalogPack.rows.find(
    (r) => String(r.skillId || r.id || "").trim() === String(skillId || "").trim(),
  ) || null;

  const groupSelectOptions = useMemo(() => {
    const set = new Set();
    for (const g of groupOptions || []) {
      const t = String(g || "").trim();
      if (t) set.add(t);
    }
    const cur = String(group || "").trim();
    if (cur) set.add(cur);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [groupOptions, group]);

  const gpioSummary = useMemo(() => {
    const list = Array.isArray(seats) ? seats : [];
    if (!list.length) return { used: 0, free: 0, total: 0 };
    let used = 0;
    for (const s of list) {
      const role = String(s.role || "").trim();
      const st = String(s.state || s.binding_state || "").toLowerCase();
      if (role || st === "claimed" || st === "verified" || st === "confirmed") used += 1;
    }
    return { used, free: Math.max(0, list.length - used), total: list.length };
  }, [seats]);

  const saveProfile = async () => {
    if (!did) return { ok: false, tone: "bad", say: "No unit selected" };
    if (!local && !writeOk) {
      return { ok: false, tone: "warn", say: "Profile edit closed for this seat" };
    }
    const guard = profileClientGuard(label, group);
    if (!guard.ok) return { ok: false, tone: "bad", say: guard.error };
    setLabel(guard.label);
    setGroup(guard.group);
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [did] }), {
      method: "PATCH",
      body: { display_label: guard.label, notification_group: guard.group },
    });
    if (!out) {
      if (local) {
        setDirty(false);
        return { ok: true, tone: "warn", say: "Identity saved locally · profile update not available here" };
      }
      return { ok: false, tone: "bad", say: act.error || "Profile change refused" };
    }
    setDirty(false);
    detailApi.reload && detailApi.reload();
    bumpLiveZones({ reason: "machine-profile", deviceId: did });
    return { ok: true, tone: "go", say: "Identity applied" };
  };

  // Zone is NOT on DeviceProfileBody. Free-text zone PATCH was a false Live plane.
  // Placement Live write = map_lat/map_lng (Places / ops map). Bulk zone_override is Fleet ops.

  const saveWifiCreds = async () => {
    if (!did) return { ok: false, tone: "bad", say: "No unit selected" };
    const reported = String(textOf(detail.ssid) || "").trim();
    const name = (wifiIntent === "password" && reported)
      ? reported
      : String(ssid || "").trim();
    if (!name) return { ok: false, tone: "bad", say: "Enter a network name" };
    if (name.length > 32) return { ok: false, tone: "bad", say: "Network name must be 32 characters or fewer" };
    const pass = String(psk || "");
    if (!pass.trim()) return { ok: false, tone: "bad", say: "Enter the Wi‑Fi password" };
    if (local) {
      softNetPut(did, { medium: "wifi", ssid: name, pskQueued: true });
      setPsk("");
      setWifiDirty(false);
      setWifiOpen(false);
      return {
        ok: true,
        tone: "warn",
        say: wifiIntent === "password"
          ? "Wi‑Fi · new password queued locally · not sent to board"
          : "Wi‑Fi · new network queued locally · not sent to board",
      };
    }
    if (!cmdOk) {
      return { ok: false, tone: "warn", say: "Commands closed for this seat" };
    }
    const dual = linkKindOf(detail, device, "") === "wifi" && lanSideLiveOf(detail, device, net);
    const ok = await confirmDeviceCommand({
      kind: "wifi_config",
      targets: [{
        id: did,
        label: String(label || did),
        ssid: reported || "",
        presence: speaking ? "Online" : "Quiet",
        link: dual ? "Wi-Fi · LAN" : "Wi-Fi",
      }],
      facts: [
        { k: "Now on", v: reported || "— none", readOnly: true },
        { k: wifiIntent === "password" ? "Stay on" : "Join", v: name, tone: name === reported ? "idle" : "warn" },
        { k: "Action", v: wifiIntent === "password" ? "Change password only" : "Join another network" },
      ],
      effects: [
        wifiIntent === "password"
          ? "Same network · only the password is sent."
          : "Board leaves the current Wi‑Fi and joins the new network · contact may drop until join.",
        "Not a factory reset · skills and other config stay on the board.",
        dual
          ? "Wi‑Fi · LAN both live · LAN can help if Wi‑Fi join fails."
          : "Wi‑Fi-only · wrong password can strand the unit until local recovery.",
      ],
      detail: "Send Wi‑Fi settings to this machine. Local preview never invents a Live switch.",
      confirmLabel: wifiIntent === "password" ? "Send new password" : "Send new network",
      ackLabel: wifiIntent === "password"
        ? "I checked the unit — send a new password for this network."
        : "I checked now-on vs join name — send the new network.",
    });
    if (!ok) return { ok: false, tone: "dim", say: "Cancelled" };
    const stageOut = await act.run(wifiOpsPath("post__devices_p_wifi-ops_stage", did), {
      method: "POST",
      body: { ssid: name },
    });
    if (!stageOut) return { ok: false, tone: "bad", say: act.error || "Stage refused" };
    const out = await act.run(wifiOpsPath("post__devices_p_wifi-ops_switch", did), {
      method: "POST",
      headers: { "Idempotency-Key": wifiOpsIdempotencyKey("switch", did) },
      body: { ssid: name, password: pass },
    });
    if (!out || !out.job) return { ok: false, tone: "bad", say: act.error || "Switch refused" };
    setPsk("");
    setWifiDirty(false);
    const polled = await wifiOpsPollJob(act, did, out.job.job_id);
    wifiOpsApi.reload && wifiOpsApi.reload();
    if (polled && polled.success) {
      const say = wifiSwitchedSay(wifiOpsStatusViewOf(wifiOpsApi.data), polled);
      if (say.tone === "go") {
        setWifiOpen(false);
        return { ok: true, tone: "go", say: say.say };
      }
    }
    if (polled && polled.completed && !polled.success) {
      return { ok: false, tone: "bad", say: polled.ackDetail || "Switch failed · device ACK" };
    }
    return {
      ok: true,
      tone: "wait",
      say: "Switch accepted (HTTP 202) · waiting for the board to confirm",
    };
  };

  const runWifiScanViaLan = async () => {
    if (!did) return;
    const claimed = String(ssid || textOf(detail.ssid) || "").trim();
    const dual = linkKindOf(detail, device, "") === "wifi" && lanSideLiveOf(detail, device, net);
    if (!dual && !local) {
      toast && toast("SSID scan needs Wi-Fi · LAN both live", "warn");
      return;
    }
    if (!local && !cmdOk) {
      toast && toast("Commands closed for this seat", "warn");
      return;
    }
    setWifiScanBusy(true);
    setWifiScan([]);
    try {
      if (local) {
        await new Promise((r) => setTimeout(r, 280));
        setWifiScan(softWifiScanViaLan(did, claimed));
        toast && toast("Network scan · local preview only · not board results", "warn");
        return;
      }
      const out = await act.run(wifiOpsPath("post__devices_p_wifi-ops_scan", did), {
        method: "POST",
        headers: { "Idempotency-Key": wifiOpsIdempotencyKey("scan", did) },
        body: { via: "lan", timeout_s: 15 },
      });
      if (!out || !out.job) {
        toast && toast(act.error || "Scan refused", "bad");
        return;
      }
      toast && toast("Scan accepted · waiting for device results", "warn");
      const polled = await wifiOpsPollJob(act, did, out.job.job_id);
      wifiOpsApi.reload && wifiOpsApi.reload();
      const hits = scanHitsFromJob(polled, wifiOpsView);
      if (hits.length) {
        setWifiScan(hits.map((h) => ({
          ...h,
          match: !!(claimed && h.id === claimed),
        })));
        toast && toast("Scan complete · " + hits.length + " network(s) from device", "go");
        return;
      }
      toast && toast("Scan finished · no networks in ACK/telemetry yet", "warn");
    } finally {
      setWifiScanBusy(false);
    }
  };

  const speaking = !!(device && device.online);

  const runRows = useMemo(() => runtimeRails.running.map((s) => {
    const cat = catalogRowOf(s.skillId);
    const gate = edgeSkillRuntimeRemoveGateOf(runtimeGateCtx(s.skillId));
    return {
      skillId: s.skillId,
      label: (cat && cat.label) || skillLabelOf(s.skillId),
      say: (cat && cat.say) || skillSayOf(s.skillId),
      stateSay: "Running · device telemetry · " + (s.telemetryObservedAt || "observed"),
      artifactId: s.artifactId,
      gate,
    };
  }), [runtimeRails.running, catalogPack.rows, runtimeStatus, runtimeView]);

  const stagedRows = useMemo(() => runtimeRails.staged.map((s) => {
    const cat = catalogRowOf(s.skillId);
    const gate = edgeSkillRuntimeActivateGateOf(runtimeGateCtx(s.skillId));
    return {
      skillId: s.skillId,
      label: (cat && cat.label) || skillLabelOf(s.skillId),
      say: (cat && cat.say) || skillSayOf(s.skillId),
      stateSay: "Staged · device telemetry · tap Activate after review",
      artifactId: s.artifactId,
      gate,
    };
  }), [runtimeRails.staged, catalogPack.rows, runtimeStatus, runtimeView]);

  const pendingRows = useMemo(() => {
    const rows = [];
    for (const j of runtimeRails.pendingJobs) {
      rows.push({
        key: "job-" + j.jobId,
        skillId: j.skillId,
        label: skillLabelOf(j.skillId),
        say: (j.action || "job") + " · " + (j.state || "pending") + (j.ackDetail ? (" · " + j.ackDetail) : ""),
        tone: "wait",
      });
    }
    for (const s of runtimeRails.pendingSkills) {
      rows.push({
        key: "skill-" + s.skillId,
        skillId: s.skillId,
        label: skillLabelOf(s.skillId),
        say: "Intent dispatched · waiting for device ACK",
        tone: "wait",
      });
    }
    return rows;
  }, [runtimeRails.pendingJobs, runtimeRails.pendingSkills]);

  const failedRows = useMemo(() => runtimeRails.failedJobs.map((j) => ({
    key: "fail-" + j.jobId,
    skillId: j.skillId,
    label: skillLabelOf(j.skillId),
    say: (j.state || "failed") + (j.ackDetail ? (" · " + j.ackDetail) : ""),
  })), [runtimeRails.failedJobs]);

  const marketRows = useMemo(() => runtimeRails.market.map((item) => {
    const skillId = String(item.skillId || item.id || "").trim();
    const artifact = edgeSkillRuntimeArtifactForSkill(runtimeArtifactsView, skillId);
    const gate = edgeSkillRuntimeInstallGateOf(runtimeGateCtx(skillId, item, artifact));
    return {
      ...item,
      skillId,
      label: item.label || skillLabelOf(skillId),
      say: item.say || skillSayOf(skillId) || "",
      gate,
      stateSay: catalogPack.honesty === "soft_preview"
        ? "Local catalog · market only · not board inventory"
        : (gate.allowed ? "Available · runtime install ready" : gate.label),
    };
  }), [
    runtimeRails.market,
    runtimeArtifactsView,
    runtimeStatus,
    runtimeView,
    catalogPack.honesty,
  ]);

  const runN = runRows.length;
  const stagedN = stagedRows.length;
  const pendingN = pendingRows.length;
  const marketN = marketRows.length;
  const inventorySay = edgeSkillRuntimeInventorySay(runtimeRails.inventory);

  const groupPickOpts = [
    { id: "", label: "Unassigned", say: "No dispatch group" },
    ...groupSelectOptions.map((g) => ({ id: g, label: g, say: "Dispatch group" })),
  ];
  const st = (id) => fieldStatus[id] || null;
  const busy = !!act.busy;

  const reportedLink = linkFactLabelOf(detail, device, net) || "—";
  const linkKind = linkKindOf(detail, device, "");
  const wifiLive = linkKind === "wifi";
  const dualLanWifi = wifiLive && lanSideLiveOf(detail, device, net);
  const liveSsid = String(ssid || textOf(detail.ssid) || "").trim();
  const reportedBoardSku = textOf(detail.board_profile, device && device.kind);
  const reportedBoard = boardKindSay(reportedBoardSku);
  const reportedIp = liveIp || "—";
  const reportedMask = liveMask || "—";
  const reportedGw = liveGw || "—";

  const scrollBody = html`
    <${M.div} className="mh-sheet mh-estate__unit-scroll" data-cmc-l0-unit="1" data-calm="1" data-dense="0"
      data-truth="live-only" data-comfort="1" data-layout="facts-stack" data-breathe="1"
      initial=${{ opacity: 0, y: 8 }}
      animate=${{ opacity: 1, y: 0 }}
      transition=${{ type: "tween", duration: 0.18, ease: [0.16, 1, 0.3, 1] }}>

      <div className="mh-split__cols" data-layout="facts-stack">
        <section className="mh-estate__lane mh-estate__lane--dense mh-split__facts"
          aria-label="Hardware · reported and editable">
          <header className="mh-estate__lane-h">
            <span>Hardware</span>
          </header>
          <div className="mh-estate__facts" role="group" aria-label="Unit facts and edits"
            data-cmc-drawer-id-ok=${(local || writeOk) ? "1" : "0"}>
            <${EditRow} id="name" label="Name" display=${label || "—"}
              hint="PATCH profile · display_label"
              open=${editId === "name"} onToggle=${toggleEdit} status=${st("name")} busy=${busy}
              disabled=${!local && !writeOk}
              onDispatch=${() => runDispatch("name", saveProfile)}>
              <input className="inp mh-estate__inp" value=${label} maxLength=${64}
                onChange=${(e)=>{setLabel(e.target.value);setDirty(true);}}/>
            <//>
            <${EditRow} id="group" label="Group" display=${group || "Unassigned"}
              hint="PATCH profile · notification_group"
              open=${editId === "group"} onToggle=${toggleEdit} status=${st("group")} busy=${busy}
              disabled=${!local && !writeOk}
              onDispatch=${() => runDispatch("group", saveProfile)}>
              <${EstatePick} card compact autoOpen=${editId === "group"}
                label="Group" value=${group || ""} options=${groupPickOpts}
                onChange=${(v)=>{setGroup(v);setDirty(true);}}/>
            <//>
            <${FactRow} keyFact label="Presence" value=${speaking ? "Online" : "Quiet"}/>
            <${FactRow} label="Site" value=${site || "—"}/>
            <${FactRow} keyFact label="Zone" value=${zone || "—"}
              hint="Derived from map geometry when you save a pin"/>
            <div className="mh-loc" data-open=${locOpen ? "1" : "0"} data-dirty=${locDirty ? "1" : "0"}>
              <button type="button" className="mh-editrow mh-loc__sum" data-fact="1" data-key="1"
                aria-expanded=${locOpen ? "true" : "false"}
                title="Firmware auto-locate when reported · click to enter / fine-tune on mini map"
                onClick=${() => setLocOpen((v) => !v)}>
                <div className="mh-loc__main">
                  <span className="mh-editrow__k">Location</span>
                  <b className="mh-editrow__v mh-loc__coord">${fmtCoord(point || savedPoint)}</b>
                  <em className="mh-loc__edit" aria-hidden="true">${locOpen ? "▾" : "▸"} edit</em>
                </div>
                <small className="mh-loc__src">${locationSourceSay(locSource)}</small>
              </button>
              ${locOpen ? html`
                <div className="mh-loc__body">
                  <div className="mh-loc__status" data-tone=${pointInside ? "go" : (point || savedPoint ? "warn" : "dim")}>
                    <span>Source</span>
                    <b>${locationSourceSay(locSource)}</b>
                    <span>Coords</span>
                    <b>${fmtCoord(point || savedPoint)}</b>
                  </div>
                  <div className="mh-loc__inputs">
                    <label className="mux__field mux__field--edit"><u>Latitude</u>
                      <input className="inp" inputMode="decimal" value=${latDraft}
                        placeholder="e.g. 3.14825"
                        onChange=${(e) => { setLatDraft(e.target.value); setLocDirty(true); }}
                        onBlur=${() => applyDraftPoint(latDraft, lngDraft)}/></label>
                    <label className="mux__field mux__field--edit"><u>Longitude</u>
                      <input className="inp" inputMode="decimal" value=${lngDraft}
                        placeholder="e.g. 101.69540"
                        onChange=${(e) => { setLngDraft(e.target.value); setLocDirty(true); }}
                        onBlur=${() => applyDraftPoint(latDraft, lngDraft)}/></label>
                  </div>
                  <div className="mh-loc__zone">
                    <${EstatePick} card label="Zone boundary" value=${zoneId}
                      placeholder="Choose surveyed Zone…"
                      autoOpen=${false}
                      options=${[
                        { id: "", label: "Choose surveyed Zone…", say: "Need a boundary before map pin" },
                        ...zones.map((z) => ({
                          id: String(z.zone_id || z.id || ""),
                          label: String(z.label || z.zone_id || z.id || "Zone"),
                          say: [
                            z.soft_site_label || z.site_id
                              ? ("Site · " + (z.soft_site_label || z.site_id))
                              : "",
                            Array.isArray(z.ring) ? (z.ring.length + " ring pts") : "",
                          ].filter(Boolean).join(" · ") || "Surveyed geometry",
                        })),
                      ]}
                      onChange=${(v) => { setZoneId(v); setLocDirty(true); }}/>
                  </div>
                  <p className="mh-loc__hint" data-tone=${pointInside ? "go" : "warn"}>
                    ${selectedZone
                      ? (pointInside
                        ? ("Inside " + (selectedZone.label || selectedZone.zone_id || selectedZone.id))
                        : ("Outside " + (selectedZone.label || selectedZone.zone_id || selectedZone.id) + " · retap map"))
                      : (zones.length
                        ? "Pick a Zone boundary, then type coords or tap the mini map"
                        : "No surveyed Zone for this site yet")}
                  </p>
                  <div className="mh-loc__map">
                    <${ZoneMiniMap} zones=${zones} selectedId=${zoneId} point=${point}
                      interactive=${!!selectedZone}
                      onPoint=${(next) => {
                        if (selectedZone && !pointInZone(next, selectedZone.ring)) {
                          toast && toast("That point is outside the selected Zone", "warn");
                          return;
                        }
                        setPoint(next);
                        setLatDraft(String(Number(next[1]).toFixed(5)));
                        setLngDraft(String(Number(next[0]).toFixed(5)));
                        setLocDirty(true);
                      }}/>
                  </div>
                  <div className="mux__panel-acts">
                    <button type="button" className="btn btn--go btn--sm"
                      data-cmc-drawer-place-ok=${(local || writeOk) ? "1" : "0"}
                      disabled=${!locDirty || act.busy || (!local && !writeOk)}
                      onClick=${() => runDispatch("location", saveLocation)}>
                      ${act.busy ? "Saving…" : "Save location"}
                    </button>
                    ${st("location") ? html`<small data-tone=${st("location").tone || "dim"}>
                      ${st("location").say || ""}</small>` : null}
                  </div>
                </div>` : null}
            </div>
            <div className="mh-tf" data-key="1" data-empty=${tfPresence === "Empty" || tfPresence === "—" ? "1" : "0"}
              data-cmc-drawer-tf-ok=${(local || cmdOk) ? "1" : "0"}
              aria-label=${"TF card · " + tfPresence + (tfUsedPct != null ? (" · " + tfUsedPct + "% used") : "")}>
              <div className="mh-loc__main">
                <span className="mh-editrow__k">TF card</span>
                <b className="mh-editrow__v">${tfPresence}</b>
                ${card && card.present && !card.mounted ? html`
                  <button type="button" className="mh-tf__format" disabled=${!!act.busy || (!local && !cmdOk)}
                    title=${local ? "Mount TF · localhost preview" : "Mount TF · Live dispatch, wait ACK"}
                    onClick=${(e) => { stop(e); mountTf(); }}>
                    Mount
                  </button>` : null}
                ${card && card.mounted ? html`
                  <button type="button" className="mh-tf__format" disabled=${!!act.busy || (!local && !cmdOk)}
                    title=${local ? "Unmount TF · localhost preview" : "Unmount TF · Live dispatch, wait ACK"}
                    onClick=${(e) => { stop(e); unmountTf(); }}>
                    Unmount
                  </button>` : null}
                <button type="button" className="mh-tf__format" disabled=${!!act.busy || tfPresence === "Empty" || tfPresence === "—" || (!local && !cmdOk)}
                  title=${local ? "Format TF · localhost preview" : "Format TF · Live dispatch, wait ACK + telemetry"}
                  onClick=${(e) => { stop(e); formatTf(); }}>
                  Format
                </button>
              </div>
              <div className="mh-tf__meter" role="meter"
                aria-valuemin="0" aria-valuemax="100"
                aria-valuenow=${tfUsedPct != null ? tfUsedPct : undefined}
                aria-label="TF capacity used"
                data-known=${tfUsedPct != null ? "1" : "0"}>
                <i style=${{ width: (tfUsedPct != null ? tfUsedPct : 0) + "%" }} aria-hidden="true"/>
              </div>
              <small className="mh-tf__say">${tfMeterSay}</small>
            </div>
            <${FactRow} label="MAC" value=${mac || "—"}/>
            <${FactRow} keyFact label="Firmware" value=${fw || "—"}
              hint=${upgradeOn
                ? ("Board image peek · " + (fwLatest || "update") + " available · SA executes")
                : "Board image · skill pushes do not rewrite this"}/>
            <${FactRow} label="Board" value=${reportedBoard} hint=${reportedBoardSku || ""}/>
            <${FactRow} keyFact label="Link" value=${reportedLink}
              hint="Wi-Fi alone · LAN only when wired Live · PoE shows as PoE/LAN"/>
            ${wifiLive ? html`
              <div className="mh-wifi" data-open=${wifiOpen ? "1" : "0"}
                data-dual=${dualLanWifi ? "1" : "0"}
                data-empty=${liveSsid ? "0" : "1"}
                data-intent=${wifiIntent}
                data-hot="1">
                <button type="button" className="mh-editrow mh-wifi__sum" data-fact="1" data-key="1"
                  data-ssid-ro="1"
                  aria-expanded=${wifiOpen ? "true" : "false"}
                  title="Open to change Wi‑Fi password or join another network"
                  onClick=${() => setWifiOpen((v) => !v)}>
                  <div className="mh-loc__main">
                    <span className="mh-editrow__k">Wi‑Fi</span>
                    <b className="mh-editrow__v" data-ro="1">${liveSsid || "Not connected"}</b>
                    <em className="mh-loc__edit" aria-hidden="true">
                      ${wifiOpen ? "▾" : "▸"} change
                    </em>
                  </div>
                  <small className="mh-wifi__src">
                    ${liveSsid
                      ? ("Now on · " + liveSsid + (dualLanWifi ? " · LAN available" : ""))
                      : (dualLanWifi ? "No network yet · can scan via LAN" : "No network name reported")}
                  </small>
                </button>
                ${wifiOpen ? html`
                  <div className="mh-wifi__body">
                    <div className="mh-wifi__now" data-ro="1">
                      <span>Now connected to</span>
                      <b>${liveSsid || "— none reported"}</b>
                    </div>

                    <div className="mh-wifi__intents" role="radiogroup" aria-label="What do you want to do">
                      <button type="button" role="radio" className="mh-wifi__intent"
                        aria-checked=${wifiIntent === "password" ? "true" : "false"}
                        data-on=${wifiIntent === "password" ? "1" : "0"}
                        disabled=${!liveSsid || busy}
                        onClick=${() => {
                          setWifiIntent("password");
                          if (liveSsid) setSsid(liveSsid);
                          setWifiDirty(!!String(psk || "").trim());
                        }}>
                        <b>Change password</b>
                        <small>Stay on the same network</small>
                      </button>
                      <button type="button" role="radio" className="mh-wifi__intent"
                        aria-checked=${wifiIntent === "join" ? "true" : "false"}
                        data-on=${wifiIntent === "join" ? "1" : "0"}
                        disabled=${busy}
                        onClick=${() => {
                          setWifiIntent("join");
                          setWifiDirty(true);
                        }}>
                        <b>Join another network</b>
                        <small>New name · may drop briefly</small>
                      </button>
                    </div>

                    ${wifiIntent === "join" && dualLanWifi ? html`
                      <div className="mh-wifi__scan">
                        <button type="button" className="mh-wifi__scan-btn"
                          data-cmc-drawer-scan-ok=${(local || cmdOk) ? "1" : "0"}
                          disabled=${wifiScanBusy || busy || (!local && !cmdOk)}
                          onClick=${(e) => { stop(e); runWifiScanViaLan(); }}>
                          ${wifiScanBusy ? "Looking for networks…" : "Find networks nearby (via LAN)"}
                        </button>
                        <small>${local
                          ? "Localhost preview · not board results"
                          : "Uses the LAN side so Wi‑Fi can switch safely · 2.4 GHz scan only on C6 · 5 GHz not joinable"}</small>
                      </div>
                      ${wifiScan.length ? html`
                        <div className="mh-wifi__hits" role="listbox" aria-label="Networks found">
                          ${wifiScan.map((hit) => html`
                            <button key=${hit.id} type="button" className="mh-wifi__hit"
                              data-match=${hit.match ? "1" : "0"}
                              data-band-usable=${hit.bandUsable === false ? "0" : "1"}
                              role="option"
                              disabled=${hit.bandUsable === false}
                              onClick=${() => {
                                if (hit.bandUsable === false) return;
                                setSsid(hit.id);
                                setWifiIntent("join");
                                setWifiDirty(true);
                                setWifiScan([]);
                              }}>
                              <b>${hit.label}</b>
                              <small>${hit.say}</small>
                            </button>`)}
                        </div>` : null}
                    ` : null}

                    <div className="mh-wifi__form" data-intent=${wifiIntent}>
                      ${wifiIntent === "password" ? html`
                        <div className="mh-wifi__locked" data-ro="1">
                          <span>Network</span>
                          <b>${liveSsid}</b>
                          <em>unchanged</em>
                        </div>
                      ` : html`
                        <label className="mh-wifi__field">
                          <span>Network name</span>
                          <input className="inp" value=${ssid} maxLength=${32}
                            placeholder="Type the Wi‑Fi name"
                            aria-label="Network name to join"
                            onChange=${(e) => { setSsid(e.target.value); setWifiDirty(true); }}/>
                          ${liveSsid ? html`
                            <button type="button" className="mh-wifi__use-now"
                              onClick=${() => { setSsid(liveSsid); setWifiDirty(true); }}>
                              Use current name
                            </button>` : null}
                        </label>
                      `}
                      <label className="mh-wifi__field">
                        <span>${wifiIntent === "password" ? "New password" : "Password"}</span>
                        <input className="inp" type="password" value=${psk}
                          placeholder=${wifiIntent === "password" ? "New Wi‑Fi password" : "Wi‑Fi password"}
                          aria-label="Wi-Fi password"
                          onChange=${(e) => { setPsk(e.target.value); setWifiDirty(true); }}/>
                      </label>
                    </div>

                    <p className="mh-wifi__consequence" data-tone=${wifiIntent === "join" ? "warn" : "dim"}>
                      ${wifiIntent === "password"
                        ? "Sends a new password for the same network. Contact usually stays up."
                        : "Board leaves the current Wi‑Fi and joins the new name. Contact may drop until it connects."}
                    </p>

                    <div className="mh-wifi__acts">
                      <button type="button" className="btn btn--go btn--sm mh-wifi__send"
                        data-cmc-drawer-wifi-ok=${(local || cmdOk) ? "1" : "0"}
                        disabled=${busy
                          || !String(psk || "").trim()
                          || (wifiIntent === "join" && !String(ssid || "").trim())
                          || (wifiIntent === "password" && !liveSsid)
                          || (!wifiDirty && wifiIntent === "join")
                          || (!local && !cmdOk)}
                        onClick=${() => {
                          runDispatch("wifi", saveWifiCreds);
                        }}>
                        ${busy
                          ? "Sending…"
                          : (local
                            ? "Save locally"
                            : (wifiIntent === "password" ? "Send new password" : "Send new network"))}
                      </button>
                      <button type="button" className="mh-wifi__cancel"
                        onClick=${() => {
                          setWifiOpen(false);
                          setPsk("");
                          setWifiDirty(false);
                          setWifiScan([]);
                          setSsid(liveSsid || "");
                          setWifiIntent(liveSsid ? "password" : "join");
                        }}>Close</button>
                      ${!local && wifiSwitchSay ? html`<small data-tone=${wifiSwitchSay.tone || "dim"}>
                        ${wifiSwitchSay.say || ""}</small>` : null}
                    </div>
                  </div>` : null}
              </div>` : null}
            <details className="mh-netstack" data-order="gw-mask-ip" data-open=${netOpen ? "1" : "0"}
              aria-label="Network addresses"
              title="Reported by heartbeat — not inventable here"
              onToggle=${(e) => setNetOpen(!!e.target.open)}>
              <summary className="mh-netstack__sum">
                <span className="mh-netstack__sum-k">IP</span>
                <b className="mh-netstack__sum-v">${reportedIp}</b>
                <em className="mh-netstack__sum-chev" aria-hidden="true">${netOpen ? "▾" : "▸"}</em>
                <small className="mh-netstack__sum-hint">${netOpen ? "Hide LAN" : "LAN · Gateway · Mask"}</small>
              </summary>
              <div className="mh-netstack__body">
                <header className="mh-netstack__h">LAN · Reported</header>
                <div className="mh-netstack__line">
                  <span>Gateway</span>
                  <b>${reportedGw}</b>
                </div>
                <div className="mh-netstack__line">
                  <span>Subnet Mask</span>
                  <b>${reportedMask}</b>
                </div>
                <div className="mh-netstack__line" data-ip="1">
                  <span>IP</span>
                  <b>${reportedIp}</b>
                </div>
              </div>
            </details>
            <${FactRow} label="Edge AI" value=${edgeAi.label}/>
            <${FactRow} label="GPIO" value=${gpioSummary.total
              ? (gpioSummary.used + " used · " + gpioSummary.free + " free")
              : "Not reported"}/>
            <${FactRow} label="uDock" value=${udock.label}/>
            <${FactRow} label="Mesh" value=${mesh.label}/>
          </div>

          <div className="mh-estate__ops-plane" data-plane="1">
            <header className="mh-estate__lane-h mh-estate__lane-h--ops">
              <span>Board ops</span>
            </header>
            <${MachineInspectOps} deviceId=${did} detail=${detail} device=${device}
              toast=${toast} facet="board" flat=${true}/>
          </div>
        </section>

        <section className="mh-skills" data-rails="cards" aria-label="Skills · runtime telemetry">
            <header className="mh-skills__head">
              <span>Skills</span>
              <small>Running/Staged from device telemetry · catalog is market only · board image untouched</small>
            </header>

            ${runtimeProductOn
              ? html`<p className="mh-skills__inv note">${inventorySay}${runtimeViewOnly ? " · look only — this seat cannot install yet" : ""}</p>`
              : html`<p className="mh-skills__inv note">${EDGE_SKILL_RUNTIME_INSTALL_REFUSAL}</p>`}

            <div className="mh-skills__band" data-st="run" aria-label="Running on board">
              <div className="mh-skills__band-h">
                <b>Running</b>
                <em>Seen on the board</em>
                <strong>${runN}</strong>
              </div>
              ${runRows.length
                ? html`<div className="mh-skcards" data-st="run">
                    ${runRows.map((c) => html`
                      <div key=${"run-" + c.skillId} className="mh-skcard" data-st="run">
                        <i className="mh-skcard__pulse" aria-hidden="true"></i>
                        <span className="mh-skcard__tag">Running</span>
                        <b className="mh-skcard__name">${c.label}</b>
                        <small className="mh-skcard__say">${c.say || "Board ability"}</small>
                        <button type="button" className="mh-skcard__act-btn"
                          disabled=${busy || !c.gate.allowed}
                          title=${c.gate.label}
                          onClick=${() => removeActiveSkill(c.skillId)}>
                          ${c.gate.allowed ? "Remove" : "Blocked"}
                        </button>
                      </div>`)}
                  </div>`
                : html`<p className="mh-skills__empty">No runtime skills running · telemetry not observed yet</p>`}
            </div>

            <div className="mh-skills__band" data-st="wait" aria-label="Staged skills">
              <div className="mh-skills__band-h">
                <b>Staged</b>
                <em>Waiting on the board</em>
                <strong>${stagedN}</strong>
              </div>
              ${stagedRows.length
                ? html`<div className="mh-skcards" data-st="wait">
                    ${stagedRows.map((c) => html`
                      <div key=${"stg-" + c.skillId} className="mh-skcard" data-st="wait">
                        <span className="mh-skcard__tag">Staged</span>
                        <b className="mh-skcard__name">${c.label}</b>
                        <small className="mh-skcard__say">${c.say || c.stateSay}</small>
                        <button type="button" className="mh-skcard__act-btn"
                          disabled=${busy || !c.gate.allowed}
                          title=${c.gate.label}
                          onClick=${() => activateStagedSkill(c.skillId)}>
                          ${c.gate.allowed ? "Activate" : "Blocked"}
                        </button>
                      </div>`)}
                  </div>`
                : html`<p className="mh-skills__empty">No staged skills</p>`}
            </div>

            ${pendingN || failedRows.length
              ? html`<div className="mh-skills__band" data-st="pending" aria-label="Pending jobs">
                  <div className="mh-skills__band-h">
                    <b>Pending / failed</b>
                    <em>dispatch until device ACK</em>
                    <strong>${pendingN + failedRows.length}</strong>
                  </div>
                  <div className="mh-skcards" data-st="pending">
                    ${pendingRows.map((c) => html`
                      <div key=${c.key} className="mh-skcard" data-st="wait">
                        <span className="mh-skcard__tag">Pending</span>
                        <b className="mh-skcard__name">${c.label}</b>
                        <small className="mh-skcard__say">${c.say}</small>
                      </div>`)}
                    ${failedRows.map((c) => html`
                      <div key=${c.key} className="mh-skcard" data-st="bad">
                        <span className="mh-skcard__tag">Failed</span>
                        <b className="mh-skcard__name">${c.label}</b>
                        <small className="mh-skcard__say">${c.say}</small>
                      </div>`)}
                  </div>
                </div>`
              : null}

            <div className="mh-skills__band" data-st="push" aria-label="Catalog market">
              <div className="mh-skills__band-h">
                <b>Market</b>
                <em>${catalogPack.label} · available only · never Running</em>
                <strong>${marketN}</strong>
                <button type="button" className="mh-skills__more"
                  title="Open skill catalog for this unit"
                  onClick=${(e) => {
                    stop(e);
                    go("/skills/assign" + (did ? "?device=" + encodeURIComponent(did) : ""));
                  }}>
                  More +
                </button>
              </div>
              ${catalogPack.unavailable
                ? html`<${Alert} tone="warn" title="Edge skill catalog unavailable"
                    say=${catalogPack.label + " · read-only until Live catalog is armed."}/>`
                : null}
              ${runtimeViewOnly && !local
                ? html`<p className="mh-skills__empty">Look only — this seat cannot install skills yet</p>`
                : marketRows.length
                  ? html`<div className="mh-skcards" data-st="off">
                      ${marketRows.map((c) => html`
                        <div key=${"mkt-" + c.skillId} className="mh-skcard" data-st="off">
                          <span className="mh-skcard__tag">${catalogPack.honesty === "soft_preview" ? "Local" : "Market"}</span>
                          <b className="mh-skcard__name">${c.label}</b>
                          <small className="mh-skcard__say">${c.stateSay}</small>
                          <button type="button" className="mh-skcard__act-btn"
                            disabled=${!c.gate.allowed || busy}
                            title=${c.gate.label}
                            onClick=${() => installCatalogSkill(c)}>
                            ${c.gate.allowed ? "Install" : "Blocked"}
                          </button>
                        </div>`)}
                    </div>`
                  : html`<p className="mh-skills__empty">Every catalog skill is on device or catalog is empty</p>`}
              <div className="mh-skills__dispatch">
                <p>${runtimeOpsOn
                  ? "The board must confirm before a skill is on. Wait for that confirm."
                  : EDGE_SKILL_RUNTIME_INSTALL_REFUSAL}</p>
                <button type="button" className="mh-estate__foot-btn"
                  disabled=${busy || !runtimeProductOn}
                  onClick=${() => {
                    reloadRuntime();
                    catalogListApi.reload && catalogListApi.reload();
                    toast && toast("Runtime + catalog re-read", "go");
                  }}>
                  Read the board again
                </button>
              </div>
            </div>

            <div className="mh-skills__rail" data-rail="board" aria-label="Board image · tenant peek">
              <div className="mh-skills__rail-h">
                <b>Board image</b>
                <em>Tenant peek only · SA pushes full architecture OTA</em>
              </div>
              <div className="mh-skills__board" data-need=${upgradeOn ? "1" : "0"}>
                <div className="mh-skills__board-row">
                  <span>On board</span><b>${fw || "—"}</b>
                </div>
                <div className="mh-skills__board-row">
                  <span>Available</span>
                  <b data-tone=${upgradeOn ? "go" : "dim"}>
                    ${upgradeOn ? (fwLatest || "Update flagged") : "Up to date"}
                  </b>
                </div>
                ${upgradeOn && fwNotes
                  ? html`<p className="mh-skills__board-notes">${fwNotes}</p>`
                  : upgradeOn
                    ? html`<p className="mh-skills__board-notes">
                        Version notes not published yet · SA owns the board image desk.
                      </p>`
                    : html`<p className="mh-skills__board-notes">
                        No board-image update required. Skill pushes above never rewrite firmware.
                      </p>`}
              </div>
            </div>
          </section>
      </div>
    <//>`;

  const body = html`
    ${detailApi.error ? html`<${Alert} tone="warn" title="Live detail is partial"
      say=${cmcPlainOf(detailApi.error)}/>` : null}

    <header className="mh-sheet__hero mh-estate__unit-head" data-live=${speaking ? "1" : "0"} data-cmc-unit-card="1" data-dense="0" data-breathe="1"
      data-cmc-machine-home=${machineHome}>
      <div className="mh-estate__unit-id">
        <div className="mh-estate__unit-titleline">
          <span className="mh-sheet__live mh-estate__unit-live" data-tone=${speaking ? "go" : "off"}>
            ${speaking ? "Online" : "Quiet"}
          </span>
          <h2 className="mh-estate__unit-name">${label || did}</h2>
        </div>
        <code className="mh-estate__unit-code">${did}</code>
      </div>
      <div className="mh-estate__unit-head-tools">
        <button type="button" className="mh-estate__close" aria-label="Close machine"
          onClick=${(e) => { stop(e); onClose && onClose(); }}>Close</button>
      </div>
    </header>

    <div className="mh-estate__unit-scrollbox">
      ${scrollBody}
    </div>`;

  if (docked) {
    return html`
      <div className="machine-v2__drawer mux__panel-body mh-signal-dock mh-sheet mh-estate__unit"
        role="dialog" aria-modal="false"
        aria-label=${"Machine · " + (label || did)} data-cmc-unit-card="1" data-taste="ops">
        ${body}
      </div>`;
  }

  return html`
    <${M.div} className="machine-v2__scrim" role="presentation"
      initial=${{ opacity:0 }} animate=${{ opacity:1 }} exit=${{ opacity:0 }}
      onClick=${onClose}>
      <${M.aside} className="machine-v2__drawer mh-sheet mh-estate__unit"
        role="dialog" aria-modal="true"
        aria-label=${"Machine · " + (label || did)}
        initial=${{ x: "100%" }} animate=${{ x: 0 }} exit=${{ x: "100%" }}
        transition=${SOFT} onClick=${stop}>
        <header className="machine-v2__drawer-head">
          <div className="machine-v2__drawer-id" data-live=${speaking ? "1" : "0"}>
            <i/><span><small>${speaking ? "Online" : "Quiet"}</small>
              <h2>${label || did}</h2><code>${did}</code></span>
          </div>
          <div className="machine-v2__drawer-actions">
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${()=>{ detailApi.reload && detailApi.reload(); reloadRuntime();
                toast && toast("Machine facts re-read", "go"); }}
              disabled=${detailApi.loading}><${Ico} d="refresh" w=${12}/> Re-read</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => go("/skills/assign" + (did ? "?device=" + encodeURIComponent(did) : ""))}>
              More +
            </button>
            <button type="button" className="cmc-drawer__x" aria-label="Close machine" onClick=${onClose}>×</button>
          </div>
          ${act.envelope
            ? html`<p className="note" data-cmc-last-action="1" style=${{ width:"100%", margin:"6px 0 0" }}>
                Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
            : null}
        </header>
        <div className="machine-v2__drawer-body">${body}</div>
      <//>
    <//>`;
}
